import Seo from "@/components/Seo";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { MANIFESTO, SERVICES } from "@/data/site";

const STACK = [
  "Python", "FastAPI", "LangChain", "OpenAI", "Pinecone", "Milvus",
  "n8n", "Redis", "PostgreSQL", "Docker", "Webhooks",
  "React 19", "TypeScript", "Node.js", "Tailwind CSS", "WebSockets",
];

export default function About() {
  return (
    <>
      <Seo
        title="About — codeRealm World Services LLP"
        siteName="codeRealm"
        description="Meet codeRealm World Services LLP and its founder & CEO Soumitra Mukherjee — a senior engineering studio for AI, n8n automation and React full-stack development."
        image="/ceo.png"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Soumitra Mukherjee",
          jobTitle: "Chief Executive Officer & Founder",
          worksFor: { "@type": "Organization", name: "codeRealm World Services LLP" },
        }}
      />
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 sm:px-8 sm:pt-28">
        <MaskedLine>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">The studio</span>
        </MaskedLine>
        <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.12}>Small team.</MaskedLine>
          <MaskedLine delay={0.24}>
            <span className="text-coral">Serious</span> <span className="text-outline">systems.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.35} className="mt-8 max-w-2xl">
          <p className="text-base leading-relaxed text-muted-foreground sm:text-lg">
            codeRealm World Services LLP — formerly Soumitra Mukherjee IT
            Services LLP — is an India-based engineering studio serving clients
            worldwide. We exist at the intersection of three disciplines: AI
            development, n8n automation and React full-stack engineering.
          </p>
        </Reveal>

        <div className="mt-24 grid items-center gap-14 lg:grid-cols-[1fr_1.2fr]">
          <Reveal className="relative mx-auto w-full max-w-md lg:mx-0">
            <div aria-hidden="true" className="absolute -inset-3 rotate-2 rounded-3xl border border-coral/40" />
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src="/ceo.png"
                alt="Portrait of Soumitra Mukherjee, CEO & Founder of codeRealm"
                className="aspect-square w-full object-cover"
                loading="lazy"
                data-testid="about-ceo-portrait"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Founder</span>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                Soumitra Mukherjee — ~10 years full-stack, shipping for Europe & US clients.
              </p>
              <blockquote className="mt-8 border-l-2 border-coral pl-6 font-display text-xl font-bold leading-snug tracking-tight">
                "Automation should feel like magic and read like documentation."
              </blockquote>
            </Reveal>
          </div>
        </div>

        <div className="mt-28">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">How we work</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Engineering principles</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {MANIFESTO.map((chapter, i) => (
              <Reveal key={chapter.index} delay={i * 0.08}>
                <div
                  data-testid={`principle-card-${chapter.index}`}
                  className="group h-full rounded-2xl border border-border bg-card p-8 transition-all duration-500 hover:-translate-y-1 hover:border-coral/60"
                >
                  <span className="font-display text-5xl font-extrabold text-outline transition-all duration-500 group-hover:text-coral group-hover:[-webkit-text-stroke:0px]">
                    {chapter.index}
                  </span>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight">{chapter.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{chapter.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-28">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-coral">Stack radar</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
              Tools we reach for
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-3">
            {STACK.map((tech) => (
              <span
                key={tech}
                data-testid={`stack-chip-${tech.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="rounded-full border border-border px-5 py-2.5 font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground transition-colors duration-300 hover:border-coral hover:text-coral"
              >
                {tech}
              </span>
            ))}
          </Reveal>
          <Reveal delay={0.15} className="mt-16 grid gap-6 border-t border-border pt-10 sm:grid-cols-3">
            {SERVICES.map((s) => (
              <div key={s.id}>
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-coral">{s.tag}</span>
                <p className="mt-2 font-display text-lg font-bold tracking-tight">{s.title}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>
    </>
  );
}
